import { useState, type ReactElement } from 'react';
import { type CredentialRotationHistoryListItem } from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ManualPromotionConfirmDialogProps {
    readonly open: boolean;
    readonly attempt: CredentialRotationHistoryListItem | null;
    readonly isSubmitting: boolean;
    readonly errorMessage?: string;
    readonly onOpenChange: (open: boolean) => void;
    readonly onConfirm: (note?: string) => void;
}

function formatTargetFields(
    attempt: CredentialRotationHistoryListItem | null
): string {
    if (attempt === null) {
        return 'credential fields';
    }
    const labels = attempt.display.target_fields.map((field) => field.label);
    return labels.length === 0 ? 'credential fields' : labels.join(', ');
}

export function ManualPromotionConfirmDialog(
    props: ManualPromotionConfirmDialogProps
): ReactElement {
    const [note, setNote] = useState<string>('');
    const trimmedNote = note.trim();
    return (
        <Dialog
            open={props.open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    setNote('');
                }
                props.onOpenChange(nextOpen);
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Confirm Credential Promotion</DialogTitle>
                    <DialogDescription>
                        This promotes encrypted provisional credential material
                        into the effective credential for{' '}
                        {formatTargetFields(props.attempt)}. This action does
                        not reveal current or provisional credential material.
                    </DialogDescription>
                </DialogHeader>

                {props.errorMessage === undefined ? null : (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {props.errorMessage}
                        </AlertDescription>
                    </Alert>
                )}

                <div className="space-y-2">
                    <Label htmlFor="manual-promotion-note">
                        Administrative note
                    </Label>
                    <Input
                        id="manual-promotion-note"
                        value={note}
                        disabled={props.isSubmitting}
                        onChange={(event) => {
                            setNote(event.target.value);
                        }}
                    />
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={props.isSubmitting}
                        onClick={() => {
                            setNote('');
                            props.onOpenChange(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={
                            props.attempt === null || props.isSubmitting
                        }
                        onClick={() => {
                            props.onConfirm(
                                trimmedNote.length === 0
                                    ? undefined
                                    : trimmedNote
                            );
                        }}
                    >
                        {props.isSubmitting ? 'Confirming...' : 'Confirm'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
